/**
 * Single-tab lock for an appointment so Rejoin / multi-window
 * cannot open two active media sessions for the same user.
 *
 * Intentional JOIN / rejoin uses forceAcquireCallLock (and clears
 * leftover locks from previous appointments / multi-tab paste).
 */
const LOCK_PREFIX = "prontopsy_call_lock_";
const HEARTBEAT_MS = 3000;
// Fresh lock window — long enough for brief background throttle, short enough
// that a kicked client can rejoin without a long wait.
export const CALL_LOCK_STALE_MS = 15000;
const STALE_MS = CALL_LOCK_STALE_MS;

function key(appointmentId) {
  return `${LOCK_PREFIX}${String(appointmentId || "")}`;
}

function tabId() {
  try {
    let id = sessionStorage.getItem("prontopsy_tab_id");
    if (!id) {
      id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem("prontopsy_tab_id", id);
    }
    return id;
  } catch {
    return `t_${Date.now()}`;
  }
}

export function getTabId() {
  return tabId();
}

/** Normalize appointment id for lock keys (URL id preferred by callers). */
export function normalizeLockAppointmentId(id) {
  if (id == null || id === "") return "";
  return String(id);
}

/**
 * Remove every call lock except optionally one appointment.
 * Critical after multi-tab paste: ghost locks for call A must not
 * block call B (or even rejoin A after tabId changes).
 */
export function clearOtherCallLocks(keepAppointmentId = null) {
  const keep = keepAppointmentId ? key(keepAppointmentId) : null;
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LOCK_PREFIX) && k !== keep) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Clear all call locks (safe when leaving call UI entirely). */
export function clearAllCallLocks() {
  clearOtherCallLocks(null);
}

/**
 * Soft acquire: fails if another tab holds a non-stale lock for THIS appointment.
 */
export function tryAcquireCallLock(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return true;
  const k = key(apptId);
  const me = tabId();
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const { owner, at } = JSON.parse(raw);
      if (owner && owner !== me && Date.now() - (at || 0) < STALE_MS) {
        return false;
      }
    }
    localStorage.setItem(k, JSON.stringify({ owner: me, at: Date.now() }));
    return true;
  } catch {
    return true;
  }
}

/**
 * Hard acquire for intentional Join / Rejoin / new call.
 * Clears locks for other appointments so a previous multi-tab paste
 * cannot poison the next consultation.
 */
export function forceAcquireCallLock(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return true;
  const me = tabId();
  try {
    clearOtherCallLocks(apptId);
    localStorage.setItem(key(apptId), JSON.stringify({ owner: me, at: Date.now() }));
    return true;
  } catch {
    return true;
  }
}

/** True if this tab currently owns a non-stale lock */
export function isCallLockOwner(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return true;
  const me = tabId();
  try {
    const raw = localStorage.getItem(key(apptId));
    if (!raw) return false;
    const { owner, at } = JSON.parse(raw);
    if (!owner || owner !== me) return false;
    return Date.now() - (at || 0) < STALE_MS;
  } catch {
    return false;
  }
}

/**
 * True when another tab holds a fresh lock for this appointment only.
 */
export function isOtherTabHoldingCall(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return false;
  const me = tabId();
  try {
    const raw = localStorage.getItem(key(apptId));
    if (!raw) return false;
    const { owner, at } = JSON.parse(raw);
    if (!owner || owner === me) return false;
    return Date.now() - (at || 0) < STALE_MS;
  } catch {
    return false;
  }
}

export function renewCallLock(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return;
  const k = key(apptId);
  const me = tabId();
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const { owner } = JSON.parse(raw);
      if (owner && owner !== me) return;
    }
    localStorage.setItem(k, JSON.stringify({ owner: me, at: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Release only if this tab owns the lock */
export function releaseCallLock(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return;
  const k = key(apptId);
  const me = tabId();
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const { owner } = JSON.parse(raw);
      if (owner && owner !== me) return;
    }
    localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

/**
 * Always clear this appointment's lock (and optionally all others).
 */
export function releaseCallLockForce(appointmentId, { clearOthers = false } = {}) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  try {
    if (apptId) localStorage.removeItem(key(apptId));
    if (clearOthers) clearAllCallLocks();
  } catch {
    /* ignore */
  }
}

export function startCallLockHeartbeat(appointmentId) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId) return () => {};
  renewCallLock(apptId);
  const iv = setInterval(() => renewCallLock(apptId), HEARTBEAT_MS);
  return () => {
    clearInterval(iv);
    releaseCallLock(apptId);
  };
}

/**
 * Watch for another tab taking the lock for THIS appointment.
 * Only fires on real other-tab storage events or a very fresh foreign heartbeat.
 */
export function watchCallLockStolen(appointmentId, onStolen) {
  const apptId = normalizeLockAppointmentId(appointmentId);
  if (!apptId || typeof onStolen !== "function") return () => {};
  const k = key(apptId);
  const me = tabId();
  let fired = false;

  const handle = (e) => {
    if (fired) return;
    // storage event only fires in *other* tabs
    if (e.key !== k || !e.newValue) return;
    try {
      const { owner } = JSON.parse(e.newValue);
      if (owner && owner !== me) {
        fired = true;
        onStolen();
      }
    } catch {
      /* ignore */
    }
  };

  const poll = setInterval(() => {
    if (fired) return;
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return;
      const { owner, at } = JSON.parse(raw);
      // Very fresh only — ghost locks must not kick mid-call
      if (owner && owner !== me && Date.now() - (at || 0) < HEARTBEAT_MS * 2 + 500) {
        fired = true;
        onStolen();
      }
    } catch {
      /* ignore */
    }
  }, 2000);

  window.addEventListener("storage", handle);
  return () => {
    window.removeEventListener("storage", handle);
    clearInterval(poll);
  };
}

/** Clear local session leftovers after a call ends successfully */
export function clearLocalCallState() {
  try {
    localStorage.removeItem("joined");
    localStorage.removeItem("appointmentData");
    localStorage.removeItem("callPageLeaveFlag");
  } catch {
    /* ignore */
  }
}
